import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { convertBlobPatternsToHtml } from '../extensions/VariableBlobExtension';
import type { ContractDetailData, ClauseVersionSummary, ContractPrecedentSummary } from '../shared/playbook-types';
import { useAppContext } from '../context/AppContext';
import * as api from '../services/apiClient';
import '../styles/PlaybookCommon.css';
import '../styles/MesContratsPage.css';

interface ContractDetailProps {
    contract: ContractDetailData | null;
    isLoading: boolean;
    error?: string | null;
    onRetry?: () => void;
    onEdit?: (id: number, name: string) => void;
    onDelete?: (id: number, name: string) => void;
}

export const ContractDetail: React.FC<ContractDetailProps> = ({ contract, isLoading, error, onRetry, onEdit, onDelete }) => {
    // État pour Directives (collapsable, fermé par défaut)
    const [isDirectivesExpanded, setIsDirectivesExpanded] = useState(false);
    // États pour les clauses individuelles (toutes fermées par défaut)
    const [expandedClauseIds, setExpandedClauseIds] = useState<Set<number>>(new Set());
    // État pour le téléchargement du document cadre
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState<string | null>(null);
    // État pour le téléchargement des précédents
    const [downloadingPrecedentId, setDownloadingPrecedentId] = useState<number | null>(null);

    const { setActivePage, setInitialClauseId } = useAppContext();

    // Handler pour naviguer vers une clause
    const handleClauseClick = (e: React.MouseEvent, clauseId: number) => {
        e.stopPropagation();
        setInitialClauseId(clauseId);
        setActivePage('page3'); // page3 = Mes Clauses
    };

    // Reset expanded state when contract changes
    useEffect(() => {
        // Toutes les clauses fermées par défaut
        setExpandedClauseIds(new Set());
        // Directives fermé par défaut
        setIsDirectivesExpanded(false);
    }, [contract?.id]);

    // Toggle functions
    const toggleDirectives = () => setIsDirectivesExpanded(prev => !prev);

    const toggleClause = (clauseId: number) => {
        setExpandedClauseIds(prev => {
            const next = new Set(prev);
            if (next.has(clauseId)) {
                next.delete(clauseId);
            } else {
                next.add(clauseId);
            }
            return next;
        });
    };

    // Extraire le nom du fichier depuis la clé S3
    const getFrameworkFileName = (s3Key: string | null): string => {
        if (!s3Key) return 'Document Cadre';
        const filename = s3Key.split('/').pop() || 'Document Cadre';
        if (/^\d/.test(filename) && filename.includes('_')) {
            return filename.substring(filename.indexOf('_') + 1);
        }
        return filename;
    };

    // Handler pour télécharger le document cadre
    const handleDownloadFramework = async () => {
        if (!contract?.framework_s3_key || isDownloading) return;

        setIsDownloading(true);
        setDownloadError(null);

        try {
            // api.downloadFile triggers the browser download on success
            const result = await api.downloadFile(contract.framework_s3_key);

            if (!result.success) {
                setDownloadError(result.error || 'Impossible de télécharger le fichier');
            }
            // On success: download is already triggered by api.downloadFile
        } catch (error) {
            setDownloadError(error instanceof Error ? error.message : 'Échec du téléchargement');
        } finally {
            setIsDownloading(false);
        }
    };

    // Handler pour télécharger un précédent
    const handleDownloadPrecedent = async (precedent: ContractPrecedentSummary) => {
        if (downloadingPrecedentId === precedent.id) return;

        setDownloadingPrecedentId(precedent.id);

        try {
            const result = await api.downloadFile(precedent.storage_path);

            if (!result.success) {
                console.error('Erreur téléchargement précédent:', result.error);
            }
        } catch (error) {
            console.error('Échec du téléchargement:', error);
        } finally {
            setDownloadingPrecedentId(null);
        }
    };

    // Formater la date pour l'affichage
    const formatDate = (dateString: string): string => {
        try {
            return new Date(dateString).toLocaleDateString('fr-FR');
        } catch {
            return dateString;
        }
    };

    // Obtenir la classe CSS pour le badge de statut
    const getStatusBadgeClass = (status: string): string => {
        switch (status.toLowerCase()) {
            case 'analyzed':
                return 'precedent-status-analyzed';
            case 'pending':
                return 'precedent-status-pending';
            case 'error':
                return 'precedent-status-error';
            default:
                return 'precedent-status-pending';
        }
    };

    // Render versions for a clause (toutes affichées sans collapse)
    const renderVersionsForClause = (versions: ClauseVersionSummary[] | undefined) => {
        if (!versions || versions.length === 0) {
            return (
                <div className="clause-no-versions">
                    Aucune version disponible
                </div>
            );
        }

        return (
            <div className="versions-list">
                {versions.map(version => (
                    <div key={version.id} className="version-item">
                        <div className="version-item-header">
                            <span className="version-item-name">{version.name}</span>
                        </div>
                        <div className="version-item-content">
                            {version.content && (
                                <div
                                    className="version-content-text"
                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(convertBlobPatternsToHtml(version.content)) }}
                                />
                            )}
                            {version.selection_criteria && (
                                <div className="version-criteria">
                                    <span className="version-criteria-label">Critères de sélection</span>
                                    <div
                                        className="version-criteria-content"
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(version.selection_criteria) }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Render clauses with versions
    const renderClausesWithVersions = () => {
        if (!contract?.clause_types || contract.clause_types.length === 0) {
            return (
                <div className="clause-no-versions">
                    Aucune clause associée
                </div>
            );
        }

        // Trier par order (null à la fin)
        const sortedClauses = [...contract.clause_types].sort((a, b) => {
            if (a.order === null && b.order === null) return 0;
            if (a.order === null) return 1;
            if (b.order === null) return -1;
            return a.order - b.order;
        });

        return (
            <>
                {sortedClauses.map((clause, index) => {
                    const isClauseExpanded = expandedClauseIds.has(clause.id);
                    const clauseNumber = index + 1;
                    const versionsCount = clause.versions?.length || 0;

                    return (
                        <div key={clause.id} className="clause-item">
                            {/* Header cliquable pour collapse */}
                            <div
                                className="clause-item-header"
                                onClick={() => toggleClause(clause.id)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        toggleClause(clause.id);
                                    }
                                }}
                                aria-expanded={isClauseExpanded}
                            >
                                <span className="clause-item-number">{clauseNumber}.</span>
                                <span className="clause-item-name">{clause.name}</span>
                                <div className="clause-item-meta">
                                    <span className="clause-item-badge">{versionsCount} version{versionsCount > 1 ? 's' : ''}</span>
                                    <button
                                        type="button"
                                        className="clause-detail-btn"
                                        onClick={(e) => handleClauseClick(e, clause.id)}
                                        title={`Voir la clause "${clause.name}"`}
                                    >
                                        Voir détail
                                    </button>
                                    <div className={`expand-toggle ${isClauseExpanded ? 'expanded' : ''}`}>
                                        <svg
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            className={`expand-icon ${isClauseExpanded ? 'expanded' : ''}`}
                                        >
                                            <polyline points="6 9 12 15 18 9"></polyline>
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            {/* Versions */}
                            {isClauseExpanded && (
                                <div className="clause-item-body">
                                    {renderVersionsForClause(clause.versions)}
                                </div>
                            )}
                        </div>
                    );
                })}
            </>
        );
    };

    if (isLoading) {
        return (
            <div className="playbook-detail-loading">
                <div className="loading-spinner"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="playbook-error-state">
                <p>{error}</p>
                {onRetry && (
                    <button type="button" onClick={onRetry} className="retry-button">
                        Réessayer
                    </button>
                )}
            </div>
        );
    }
    if (!contract) {
        return (
            <div className="playbook-detail-empty">
                <p>Sélectionnez un contrat pour voir les détails</p>
            </div>
        );
    }

    return (
        <div className="playbook-detail">
            {/* Header avec titre + boutons Edit/Delete */}
            <div className="playbook-detail-header">
                <div className="playbook-detail-title-row">
                    <h1>{contract.name}</h1>
                    <div className="playbook-detail-actions">
                        {onEdit && (
                            <button
                                type="button"
                                className="playbook-detail-edit-btn"
                                onClick={() => onEdit(contract.id, contract.name)}
                                title="Modifier ce contrat"
                                aria-label="Modifier ce contrat"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                                </svg>
                                <span>Modifier</span>
                            </button>
                        )}
                        {onDelete && (
                            <button
                                type="button"
                                className="playbook-detail-delete-btn"
                                onClick={() => onDelete(contract.id, contract.name)}
                                title="Supprimer ce contrat"
                                aria-label="Supprimer ce contrat"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 6h18"></path>
                                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                                </svg>
                                <span>Supprimer</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Section Directives - COLLAPSABLE (fermé par défaut) */}
            <div className={`directives-card ${isDirectivesExpanded ? 'expanded' : ''}`}>
                <div
                    className="directives-header"
                    onClick={toggleDirectives}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleDirectives();
                        }
                    }}
                    aria-expanded={isDirectivesExpanded}
                >
                    <div className="directives-header-left">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="icon-directives">
                            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                            <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                            <path d="m9 14 2 2 4-4"></path>
                        </svg>
                        <span className="directives-title">Directives</span>
                    </div>
                    <div className="expand-toggle">
                        <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            className={`expand-icon ${isDirectivesExpanded ? 'expanded' : ''}`}
                        >
                            <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                    </div>
                </div>
                {isDirectivesExpanded && (
                    <div className="directives-content">
                        {contract.note ? (
                            <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(contract.note) }} />
                        ) : (
                            <p className="empty-text">Aucune directive définie</p>
                        )}
                    </div>
                )}
            </div>

            {/* Section Document Cadre (toujours ouvert, pas de collapse) */}
            {contract.framework_s3_key && (
                <div className="playbook-card">
                    <div className="playbook-card-header">
                        {/* Icône document avec lignes (ancienne icône de directives) */}
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="icon-note">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                            <line x1="16" y1="13" x2="8" y2="13"></line>
                            <line x1="16" y1="17" x2="8" y2="17"></line>
                            <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                        <h3>Document Cadre</h3>
                    </div>
                    <div className="playbook-card-content">
                        <div className="framework-file-row">
                            <span className="framework-filename">{getFrameworkFileName(contract.framework_s3_key)}</span>
                            <button
                                type="button"
                                className="framework-download-btn"
                                onClick={handleDownloadFramework}
                                disabled={isDownloading}
                                aria-busy={isDownloading}
                            >
                                {isDownloading ? (
                                    <>
                                        <span className="loading-spinner-small"></span>
                                        Téléchargement...
                                    </>
                                ) : (
                                    <>
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                            <polyline points="7 10 12 15 17 10"></polyline>
                                            <line x1="12" y1="15" x2="12" y2="3"></line>
                                        </svg>
                                        Télécharger
                                    </>
                                )}
                            </button>
                        </div>
                        {downloadError && (
                            <div className="framework-download-error">
                                {downloadError}
                            </div>
                        )}
                        {contract.blobs && contract.blobs.length > 0 && (
                            <div className="framework-blobs-section">
                                <div className="framework-blobs-label">Variables extraites du document cadre et des clauses associées</div>
                                <div className="framework-blobs-list">
                                    {contract.blobs.map(blob => (
                                        <span
                                            key={blob.id}
                                            className="variable-blob-node"
                                            title={blob.paragraph}
                                        >
                                            {blob.name}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Section Clauses Associées (toujours ouvert, pas de collapse) */}
            <div className="playbook-card">
                <div className="playbook-card-header">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="icon-clauses">
                        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                        <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                        <line x1="8" y1="10" x2="16" y2="10"></line>
                        <line x1="8" y1="14" x2="16" y2="14"></line>
                        <line x1="8" y1="18" x2="12" y2="18"></line>
                    </svg>
                    <h3>Clauses associées</h3>
                </div>
                <div className="playbook-card-content playbook-card-content-clauses">
                    {renderClausesWithVersions()}
                </div>
            </div>

            {/* Section Précédents (après Clauses) */}
            {contract.precedents && contract.precedents.length > 0 && (
                <div className="playbook-card">
                    <div className="playbook-card-header">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="icon-precedents">
                            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                        </svg>
                        <h3>Précédents</h3>
                    </div>
                    <div className="playbook-card-content">
                        <div className="precedents-list">
                            {contract.precedents.map(precedent => (
                                <div key={precedent.id} className="precedent-item">
                                    <span className="precedent-filename">{precedent.filename}</span>
                                    <span className={`precedent-status ${getStatusBadgeClass(precedent.status)}`}>
                                        {precedent.status}
                                    </span>
                                    <span className="precedent-date">{formatDate(precedent.created_at)}</span>
                                    <button
                                        type="button"
                                        className="precedent-download-btn"
                                        onClick={() => handleDownloadPrecedent(precedent)}
                                        disabled={downloadingPrecedentId === precedent.id}
                                        aria-busy={downloadingPrecedentId === precedent.id}
                                        title={`Télécharger ${precedent.filename}`}
                                    >
                                        {downloadingPrecedentId === precedent.id ? (
                                            <span className="loading-spinner-small"></span>
                                        ) : (
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                                <polyline points="7 10 12 15 17 10"></polyline>
                                                <line x1="12" y1="15" x2="12" y2="3"></line>
                                            </svg>
                                        )}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
